import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, FormControl, FormControlLabel, RadioGroup, Switch, Typography, Divider } from "@mui/material";
import PrimaryButton from "../../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../../../components/Buttons/SecondaryButton/SecondaryButton";
import PrimaryRadio from "../../../Buttons/PrimaryRadio/PrimaryRadio";
import PrimaryTextField from "../../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimaryDatePicker from "../../../Inputs/PrimaryDatePicker/PrimaryDatePicker";
import CityAutocomplete from "../../../Inputs/CityAutocomplete/CityAutocomplete";
import PastePreferredLanesRow from "./PastePreferredLanesRow/PastePreferredLanesRow";
import PastePostedTrucksRow from "./PastePostedTrucksRow/PastePostedTrucksRow";
import WarningMessage from "../../../WarningMessage/WarningMessage";
import useEquipmentTypes from "../../../../hooks/useEquipmentTypes/useEquipmentTypes";
import { parseAvailability, resetKnownDate, resetKnownWeekday } from "../../../../utils/formatHelpers";
import { handleAltEnter } from "../../../../utils/keyboardShortcutHelpers";

let completedRequests = 0;
let errorBalance = 0;
let removedBalance = 0;

export default function PasteCapacity({
  type,
  userIdMcld,
  activeCarrier,
  handleClose,
  setSubmitAllDisabled,
  submitAllTriggered
}) {
  const { typeLabels } = useEquipmentTypes();
  const [capacity, setCapacity] = useState("");
  const [rows, setRows] = useState([]);
  const [isDefaultMode, setIsDefaultMode] = useState(false);
  const [defaultMode, setDefaultMode] = useState("V53");
  const [isDefaultTime, setIsDefaultTime] = useState(false);
  const [defaultStartDate, setDefaultStartDate] = useState(new Date());
  const [defaultExpiryDate, setDefaultExpiryDate] = useState(new Date());
  const [isDefaultDestination, setIsDefaultDestination] = useState(true);
  const [defaultDestinationCity, setDefaultDestinationCity] = useState("");
  const [defaultDestination, setDefaultDestination] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorLabel, setErrorLabel] = useState("");

  const memoRows = useMemo(() => rows, [rows]);

  const handleReset = () => {
    setCapacity("");
    setRows([]);
    setIsDefaultMode(false);
    setIsDefaultTime(false);
    setIsDefaultDestination(false);
    setErrorLabel("");
  };

  const handleCancel = () => {
    handleReset();
    handleClose();
  };

  const handleSubmit = () => {
    if (capacity.length > 0) {
      setIsLoading(true);
      setTimeout(async () => {
        const parsedRows = await parseAvailability(capacity.trim());
        const cleanedRows = parsedRows.filter(r => r.cities.length && r.states.length);
        removedBalance = cleanedRows.length;
        setRows(cleanedRows);
        resetKnownDate();
        resetKnownWeekday();
        setCapacity("");
        setIsLoading(false);
      }, 300);
    } else {
      alert("Please enter data.");
      setIsLoading(false);
    }
  };

  const handleSwitchChange = (e, fn) => {
    fn(e.target.checked);
  };

  const handleDefaultModeChange = e => {
    setDefaultMode(e.target.value);
  };

  const handleDefaultStartDateChange = (value) => {
    setDefaultStartDate(value);
    if (value) {
      let date = new Date(value.getTime());
      if (type == "preferred-lane") {
        setDefaultExpiryDate(new Date(date.setFullYear(date.getFullYear() + 1)));
      } else if (type == "posted-truck") {
        setDefaultExpiryDate(new Date(date.setDate(date.getDate() + 1)));
      }
    } else {
      setDefaultExpiryDate(value);
    }
  };

  const checkIfComplete = () => {
    if (rows.length === completedRequests + removedBalance) {
      handleClose();
      completedRequests = 0;
      errorBalance = 0;
      removedBalance = 0;
    }
  };

  const updateCompletedCount = () => {
    completedRequests++;
    checkIfComplete();
  };

  const updateErrorBalance = (value) => {
    errorBalance = errorBalance + value;
    checkSubmitAll();
  };

  const updateRemovedBalance = (value) => {
    removedBalance = removedBalance + value;
    checkSubmitAll();
  };

  const checkSubmitAll = () => {
    setSubmitAllDisabled(
      !activeCarrier ||
      rows.length === 0 ||
      errorBalance != 0 ||
      removedBalance === rows.length
    );
  };

  useEffect(() => {
    checkSubmitAll();
  }, [activeCarrier, rows]);

  useEffect(() => {
    if (isDefaultDestination && activeCarrier) {
      setDefaultDestinationCity(activeCarrier.city?.concat(", ", activeCarrier.state));
    } else {
      setDefaultDestination({});
    }
  }, [isDefaultDestination]);

  useEffect(() => {
    completedRequests = 0;
    errorBalance = 0;
    removedBalance = 0;
    let today = new Date();
    if (type == "preferred-lane") {
      setDefaultExpiryDate(new Date(today.setFullYear(today.getFullYear() + 1)));
    } else if (type == "posted-truck") {
      setDefaultExpiryDate(new Date(today.setDate(today.getDate() + 1)));
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      handleAltEnter(e, (isLoading || !capacity.length), handleSubmit);
    };

    document.addEventListener("keydown", handleKeyDown);

    return function cleanup() {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isLoading, capacity]);

  return (
    <Box sx={{ maxHeight: "60vh", overflow: "auto" }}>
      {rows.length === 0 && (
        <Box sx={{ display: "flex" }}>
          <Box id="paste-capacity">
            <PrimaryTextField
              width="500px"
              value={capacity}
              multiline
              rows={30}
              placeholder="Please take care to enter valid data."
              onChange={setCapacity}
            />
          </Box>
          <Box sx={{ marginLeft: "20px" }}>
            <Box>
              <Box sx={{ display: "flex" }}>
                <FormControl component="fieldset">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isDefaultMode}
                        onChange={e => handleSwitchChange(e, setIsDefaultMode)}
                        value={isDefaultMode}
                        color="primary"
                      />
                    }
                    label="Select Default Mode"
                  />
                  {isDefaultMode && (
                    <>
                      <Typography variant="subhead2" color="black_solid.80">
                        Mode
                      </Typography>
                      <FormControl>
                        <RadioGroup
                          aria-labelledby="carrier-profile-radio-buttons"
                          name="row-radio-buttons-group"
                          value={defaultMode}
                          onChange={handleDefaultModeChange}
                        >
                          {typeLabels.map(type => (
                            <FormControlLabel
                              key={`equipment-type-${type.id}`}
                              value={type.id}
                              control={<PrimaryRadio />}
                              label={<Typography variant="body2" color="black_solid.60">{type.label}</Typography>}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </>
                  )}
                </FormControl>
                <FormControl component="fieldset">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isDefaultTime}
                        onChange={e => handleSwitchChange(e, setIsDefaultTime)}
                        value={isDefaultTime}
                        color="primary"
                      />
                    }
                    label="Select Default Start/Expiry Times"
                  />
                  {isDefaultTime && (
                    <>
                      <Box sx={{ marginY: "16px" }}>
                        <PrimaryDatePicker
                          label="Start Date"
                          value={defaultStartDate}
                          handleChange={handleDefaultStartDateChange}
                          width="150px"
                          disablePast
                          error={!defaultStartDate && isDefaultDestination}
                        />
                      </Box>
                      <Box>
                        <PrimaryDatePicker
                          label="Expiry Date"
                          value={defaultExpiryDate}
                          handleChange={setDefaultExpiryDate}
                          width="150px"
                          disablePast
                          error={!defaultExpiryDate && isDefaultDestination}
                        />
                      </Box>
                    </>
                  )}
                </FormControl>
              </Box>
              <Divider sx={{ marginY: "16px" }} />
              <Box>
                <FormControl component="fieldset">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isDefaultDestination}
                        onChange={e => handleSwitchChange(e, setIsDefaultDestination)}
                        value={isDefaultDestination}
                        color="primary"
                      />
                    }
                    label="Select Default Destination"
                  />
                  {isDefaultDestination && (
                    <>
                      <Typography variant="body2" color="black_solid.80" marginBottom="16px">
                        Choose your default destination
                      </Typography>
                      <CityAutocomplete label="Destination" width="240px" updateSelectedCity={setDefaultDestination} defaultInput={defaultDestinationCity} />
                    </>
                  )}
                </FormControl>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <Box sx={{ display: "flex", alignItems: "flex-end" }}>
        {errorLabel &&
          <Box sx={{ display: "flex", alignItems: "flex-end", marginRight: "22px" }}>
            <WarningMessage text={errorLabel} displayText={errorLabel} />
          </Box>
        }
        {rows.length > 0 &&
          <Typography variant="subhead1" color="black_solid.80">{`${rows.length} entries processed`}</Typography>
        }
      </Box>
      {rows.length > 0 && type == "preferred-lane" ? memoRows.map((r, i) => (
        <PastePreferredLanesRow
          key={`pasted-preferred-lane-${i}`}
          index={i}
          row={r}
          defaultMode={defaultMode}
          defaultStartDate={defaultStartDate}
          defaultExpiryDate={defaultExpiryDate}
          defaultDestination={defaultDestination}
          userIdMcld={userIdMcld}
          activeCarrier={activeCarrier}
          setErrorLabel={setErrorLabel}
          submitAllTriggered={submitAllTriggered}
          updateCompletedCount={updateCompletedCount}
          updateErrorBalance={updateErrorBalance}
          updateRemovedBalance={updateRemovedBalance}
        />
      )) : memoRows.map((r, i) => (
        <PastePostedTrucksRow
          key={`pasted-posted-truck-${i}`}
          index={i}
          row={r}
          defaultMode={defaultMode}
          defaultStartDate={defaultStartDate}
          defaultExpiryDate={defaultExpiryDate}
          defaultDestination={defaultDestination}
          userIdMcld={userIdMcld}
          activeCarrier={activeCarrier}
          setErrorLabel={setErrorLabel}
          submitAllTriggered={submitAllTriggered}
          updateCompletedCount={updateCompletedCount}
          updateErrorBalance={updateErrorBalance}
          updateRemovedBalance={updateRemovedBalance}
        />
      ))}
      {memoRows.length === 0 &&
        <Box sx={{ display: "flex", marginTop: "auto" }}>
          <Box sx={{ marginLeft: "auto", marginRight: "20px" }}>
            <SecondaryButton
              label="Cancel"
              onClick={handleCancel}
              disabled={isLoading}
            />
          </Box>
          <PrimaryButton
            label="Post"
            width="50px"
            onClick={handleSubmit}
            isLoading={isLoading}
            disabled={isLoading || !capacity || !activeCarrier}
          />
        </Box>
      }
    </Box>
  );
}

PasteCapacity.propTypes = {
  type: PropTypes.string,
  userIdMcld: PropTypes.string,
  activeCarrier: PropTypes.object,
  handleClose: PropTypes.func.isRequired,
  setSubmitAllDisabled: PropTypes.func.isRequired,
  submitAllTriggered: PropTypes.bool.isRequired
};